<template>
  <b-container class="my-5">
    <!-- Content section -->
    <b-container fluid class="consent-container">
      <response-correct-alert v-if="hasCorrectOption && consents[currentStep - 1]?.correctSelectionMessage"
        :text="consents[currentStep - 1].correctSelectionMessage" />
      <response-incorrect-alert v-if="hasErrorOption && currentConsent?.incorrectSelectionMessage"
        :text="currentConsent.incorrectSelectionMessage" />

      <!-- Consent Content -->
      <template v-if="!hasErrorOption && currentConsent">
        <b-row>
          <b-col>
            <div v-html="sanitizedContent(currentConsent.content)"></div>
          </b-col>
        </b-row>
        <!-- Option Selection -->
        <b-row>
          <b-col>
            <label class="consent-option-label" v-if="currentConsent.title">
              {{ currentConsent.title }}
              <span v-if="currentConsent.info" v-b-tooltip.hover :title="currentConsent.info">
                <b-icon class="consent-option-label__info" icon="question-circle-fill"></b-icon></span>
            </label>
            <!-- Selection when type is RADIO BUTTON -->
            <b-form-group v-if="currentConsent.optionType === 'radio'" v-slot="{ ariaDescribedby }">
              <b-form-radio v-for="item in currentConsent.consentOptions" class="mb-4" :key="item.optionID"
                v-model="selected" :aria-describedby="ariaDescribedby" name="radio-selection" :value="item.optionID">{{
                  item.optionValue }}</b-form-radio>
            </b-form-group>
            <!-- Selection when type is CHECKBOX -->
            <b-form-group v-if="currentConsent.optionType === 'check'" v-slot="{ ariaDescribedby }">
              <b-form-checkbox-group class="mb-4" id="checkbox-group-1" v-model="selected" :options="currentConsent.consentOptions.map(({ optionID, optionValue }) => ({
                text: optionValue,
                value: optionID
              }))
                " :aria-describedby="ariaDescribedby" name="check-selection"></b-form-checkbox-group>
            </b-form-group>
            <!-- Selection when type is SELECT -->
            <b-form-group v-if="currentConsent.optionType === 'select'" v-slot="{ ariaDescribedby }">
              <b-form-select class="mb-4" v-model="selected" :options="currentConsent.consentOptions.map(({ optionID, optionValue }) => ({
                text: optionValue,
                value: optionID
              }))
                " :aria-describedby="ariaDescribedby"></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>
      </template>

      <!-- Previous & Next Buttons -->
      <b-row v-if="currentConsent">
        <b-col>
          <b-button-group class="mt-5">
            <b-button class="default-button mr-2" v-show="showGoPreviousPageButton" @click="goBackPage">
              {{ backButton.text }}
            </b-button>
            <b-button class="default-button" v-show="!hasErrorOption" :disabled="disableGoNextPage" @click="goNextPage">
              {{ nextButton.text }}
            </b-button>
          </b-button-group>
        </b-col>
      </b-row>

      <signature-form :form-default-values="cumulatedFormatData" :isCRC="isCRC" :locale="locale" v-if="currentStep === consents.length" @back="goBackPage"
        @submit="goNextPage" />

			<consent-process-documentation :locale="locale" :form-default-values="cumulatedFormatData" 
        v-if="currentStep === consents.length + 1 && isCRC === true" @back="goBackPage" @submit="goNextPage" />

      <category-questionnaire-form :form-default-values="cumulatedFormatData" :locale="locale"
        v-if="(currentStep === consents.length + 1 && isCRC !== true) || (currentStep === consents.length + 2 && isCRC === true)" @back="goBackPage" @submit="goNextPage" />

      <thank-you-page :locale="locale" 
				v-if="(currentStep === consents.length + 2 && isCRC !== true) || currentStep === consents.length + 3" :text="thankYouMessage" />
    </b-container>
  </b-container>
</template>

<script>
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import SignatureForm from '@/components/RemoteConsent/SignatureForm.vue'
import ResponseCorrectAlert from '@/components/RemoteConsent/ResponseCorrectAlert.vue'
import ResponseIncorrectAlert from '@/components/RemoteConsent/ResponseIncorrectAlert.vue'
import CategoryQuestionnaireForm from '@/components/RemoteConsent/CategoryQuestionnaireForm.vue'
import ConsentProcessDocumentation from '@/components/RemoteConsent/ConsentProcessDocumentation.vue'
import ThankYouPage from '@/components/RemoteConsent/ThankYouPage.vue'

export default {
  components: {
    SignatureForm,
    ResponseCorrectAlert,
    ResponseIncorrectAlert,
    CategoryQuestionnaireForm,
		ConsentProcessDocumentation,
    ThankYouPage
  },
  props: {
    consents: Array,
    backButton: Object,
    nextButton: Object,
    finishButton: Object,
    title: String,
    subtitle: String,
    form: Object,
    thankYouMessage: String,
    locale: String,
		isCRC: Boolean
  },
  data() {
    return {
      showModal: true,
      currentStep: 0,
      selected: null,
      hasErrorOption: false,
      hasCorrectOption: false,
      cumulatedFormatData: {}
    }
  },
  mounted() {
    this.$root.$on('eligible', (participantInfo) => {
			const dateArray = new Date().toISOString().split('T')[0].split('-')
			const dateString = [...dateArray.slice(-2), ...dateArray.slice(0, 1)].join('-')
			const timeString = new Date().toLocaleTimeString()
			const todayDate = `${dateString} ${timeString}`
      this.cumulatedFormatData = { ...participantInfo, todayDate, locale: this.locale }
    })
  },
  methods: {
    sanitizedContent(content) {
      return DOMPurify.sanitize(marked(content))
    },
    goBackPage() {
      console.log('Back')

      if (this.currentStep <= 0) return

      if (!this.hasErrorOption) this.currentStep = this.currentStep - 1

      this.hasErrorOption = false
      this.hasCorrectOption = false
      this.selected = null
    },
    goNextPage(formatData) {
      // Clear previous page Correct Alert message.
      this.hasCorrectOption = false

      // If the current page have Selection Options:
      if (this.currentConsent && this.currentConsent.correctOptionID) {
        // If selection is correct, show Correct Alert message
        if (this.currentConsent.correctOptionID === this.selected) {
          this.hasCorrectOption = true
        }
        // Selection is incorrect, show Error Alert message and stay in the same page.
        else {
          this.hasErrorOption = true
          return
        }
      }

      this.cumulatedFormatData = {...this.cumulatedFormatData, ...formatData}
      this.currentStep = this.currentStep + 1
      this.selected = null
      window.scrollTo(0, 0)
    }
  },
  computed: {
    currentConsent() {
      return this.consents.length > this.currentStep ? this.consents[this.currentStep] : null
    },
    disableGoNextPage() {
      return !!this.currentConsent?.consentOptions.length && !this.selected
    },
    showGoPreviousPageButton() {
      return this.currentStep !== 0
    }
  }
}
</script>

<style>
hr {
  border-bottom: 2px solid #0078b4 !important;
  width: 25%;
  display: inline-block;
}

.consent-option-label {
  font-weight: 600;
  color: #4a4a4a;
  font-size: 14px;
  cursor: pointer;
}

.consent-option-label__info {
  cursor: help;
}

.consent-container strong {
  color: #0078b4;
  font-size: 18px;
  font-weight: 700;
  padding-top: 2.5rem;
  line-height: 21px;
}

.consent-container p,
.consent-container ul li {
  color: rgb(87, 87, 87);
  font-weight: 300;
  margin-right: 8px;
}

.default-button {
  color: #fefefe !important;
  background-color: #262262 !important;
}
</style>
